import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { Package, Users, DollarSign, CheckCircle, XCircle, Clock, ArrowUp } from 'lucide-react';
import UpgradePlanModal from './components/UpgradePlanModal';

interface GroupSubscription {
  id: string;
  name: string;
  description: string;
  createdBy: string | null;
  adminName: string;
  adminEmail: string;
  planName: string;
  planType: string;
  planPrice: number;
  planId: string | null;
  paymentStatus: 'pending' | 'approved' | 'rejected' | 'none';
  lastPaymentDate: string | null;
  subscriptionStatus: 'active' | 'expired' | 'pending' | 'locked';
  memberCount: number;
  isPayAsYouGo: boolean; // Track if this is a pay-as-you-go plan
  isFreePlan: boolean; // Track if this is a free plan
}

const SubscriptionManagement = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [groups, setGroups] = useState<GroupSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'pending' | 'expired'>('all');
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedGroupForUpgrade, setSelectedGroupForUpgrade] = useState<GroupSubscription | null>(null);

  const isSuperAdmin = userProfile?.role === 'super_admin';

  useEffect(() => {
    // Wait for userProfile so role-based filtering is correct before loading groups.
    if (!user || !userProfile) return;
    fetchGroupsWithSubscriptions();
  }, [user, userProfile, filter]);

  // Helper: filter groups so non-super-admins only see their own groups
  const filterGroupsForUser = async (groupsData: any[]): Promise<any[]> => {
    if (isSuperAdmin || !user) {
      return groupsData;
    }

    try {
      const allowedGroupIds = new Set<string>();

      // Groups where user is a member
      const { data: memberGroups, error: memberError } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id);

      if (!memberError && memberGroups) {
        memberGroups.forEach((mg: any) => {
          if (mg.group_id) {
            allowedGroupIds.add(mg.group_id);
          }
        });
      }

      // Filter to groups the user created or is a member of
      const visible = groupsData.filter((g: any) =>
        g.created_by === user.id || allowedGroupIds.has(g.id)
      );

      return visible;
    } catch (err) {
      console.error('Error filtering groups for user:', err);
      // On error, fall back to showing only groups created by the user
      return groupsData.filter((g: any) => g.created_by === user.id);
    }
  };

  const fetchGroupsWithSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch active groups with their subscription plans
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          description,
          created_by,
          subscription_plan_id,
          subscription_plans (
            id,
            plan_name,
            plan_type,
            price_vatu,
            billing_model,
            price_per_member_vatu
          ),
          user_profiles!groups_created_by_fkey (
            id,
            full_name,
            email
          )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (groupsError) {
        console.error('Error fetching groups:', groupsError);
        // Fallback: fetch without join
        const { data: fallbackGroups } = await supabase
          .from('groups')
          .select('id, name, description, created_by, subscription_plan_id')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (fallbackGroups) {
          const visibleFallback = await filterGroupsForUser(fallbackGroups);
          await processGroups(visibleFallback);
        }
        return;
      }

      if (!groupsData) {
        setGroups([]);
        setLoading(false);
        return;
      }

      const visibleGroups = await filterGroupsForUser(groupsData);
      await processGroups(visibleGroups);
    } catch (err: any) {
      console.error('Error fetching groups with subscriptions:', err);
      setError(err.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const processGroups = async (groupsData: any[]) => {
    const processedGroups: GroupSubscription[] = [];

    if (groupsData.length === 0) {
      setGroups([]);
      return;
    }

    // OPTIMIZATION: Batch fetch all data upfront instead of per-group queries
    const groupIds = groupsData.map(g => g.id);
    const creatorIds = [...new Set(groupsData.map(g => g.created_by).filter(Boolean))];

    // Batch fetch all admin profiles
    const { data: adminProfiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, email')
      .in('id', creatorIds);

    const adminMap = new Map((adminProfiles || []).map(a => [a.id, a]));

    // Batch fetch all active subscriptions for creators
    const { data: allSubscriptions } = await supabase
      .from('user_subscriptions')
      .select(`
        user_id,
        plan_id,
        subscription_status,
        subscription_plans (
          id,
          plan_name,
          plan_type,
          price_vatu,
          billing_model,
          price_per_member_vatu
        )
      `)
      .in('user_id', creatorIds)
      .eq('subscription_status', 'active')
      .order('created_at', { ascending: false });

    // Group subscriptions by user_id (get most recent for each user)
    const subscriptionMap = new Map<string, any>();
    (allSubscriptions || []).forEach((sub: any) => {
      const userId = sub.user_id;
      if (!subscriptionMap.has(userId)) {
        subscriptionMap.set(userId, sub);
      }
    });

    // Batch fetch all plans (in case we need to fetch separately)
    const planIds = [...new Set(
      Array.from(subscriptionMap.values())
        .map((s: any) => s.plan_id)
        .filter(Boolean)
    )];
    
    const { data: allPlans } = await supabase
      .from('subscription_plans')
      .select('id, plan_name, plan_type, price_vatu, billing_model, price_per_member_vatu')
      .in('id', planIds);

    const planMap = new Map((allPlans || []).map(p => [p.id, p]));

    // Batch fetch all member counts using RPC (one call per group, but batched)
    const memberCountPromises = groupIds.map(groupId => 
      supabase.rpc('get_group_member_count', { group_id_param: groupId })
    );
    const memberCountResults = await Promise.all(memberCountPromises);
    const memberCountMap = new Map(
      groupIds.map((id, idx) => [id, memberCountResults[idx].data || 0])
    );

    // Batch fetch all payment records
    const { data: allPayments } = await supabase
      .from('payment_records')
      .select('group_id, payment_status, submitted_at')
      .in('group_id', groupIds)
      .order('submitted_at', { ascending: false });

    // Group payments by group_id (get most recent for each group)
    const paymentMap = new Map<string, any>();
    (allPayments || []).forEach((payment: any) => {
      const groupId = payment.group_id;
      if (!paymentMap.has(groupId)) {
        paymentMap.set(groupId, payment);
      }
    });

    // Now process each group using the batched data
    for (const group of groupsData) {
      try {
        // Get admin info from batch
        let adminName = 'Unknown';
        let adminEmail = '';
        
        if (group.user_profiles) {
          adminName = group.user_profiles.full_name || 'Unknown';
          adminEmail = group.user_profiles.email || '';
        } else if (group.created_by) {
          const admin = adminMap.get(group.created_by);
          if (admin) {
            adminName = admin.full_name || 'Unknown';
            adminEmail = admin.email || '';
          }
        }

      // Get plan info from user's subscription (groups inherit from creator's subscription)
      let planName = 'No Plan';
      let planType = 'free';
      let planPrice = 0;
      let currentPlanId: string | null = null;
      let isPayAsYouGo = false;
      let isFreePlan = false;

      // First check if group has its own subscription_plan_id (legacy)
      if (group.subscription_plans) {
        planName = group.subscription_plans.plan_name || 'No Plan';
        planType = group.subscription_plans.plan_type || 'free';
        currentPlanId = group.subscription_plans.id || group.subscription_plan_id;
        // Check if it's a free plan
        const planNameLower = (planName || '').toLowerCase();
        isFreePlan = planType === 'free' || planNameLower.includes('free');
        // Check if it's pay-as-you-go
        const billingModel = group.subscription_plans.billing_model;
        const pricePerMember = group.subscription_plans.price_per_member_vatu;
        isPayAsYouGo = (billingModel === 'per_member' || planNameLower.includes('pay-as-you-go') || planNameLower.includes('pay as you go')) && (pricePerMember !== null && pricePerMember !== undefined);
        
        // Calculate price based on plan type
        if (isPayAsYouGo && pricePerMember) {
          // Use batched member count for pay-as-you-go
          const groupMemberCount = memberCountMap.get(group.id) || 0;
          const perMemberPrice = parseFloat(String(pricePerMember)) || 0;
          planPrice = groupMemberCount * perMemberPrice;
        } else {
          // Use flat price for non-per-member plans
          planPrice = parseFloat(String(group.subscription_plans.price_vatu)) || 0;
        }
      } else if (group.subscription_plan_id) {
        currentPlanId = group.subscription_plan_id;
        // Use batched plan data
        const plan = planMap.get(group.subscription_plan_id);
        if (plan) {
          planName = plan.plan_name || 'No Plan';
          planType = plan.plan_type || 'free';
          currentPlanId = plan.id;
          // Check if it's a free plan
          const planNameLower = (planName || '').toLowerCase();
          isFreePlan = planType === 'free' || planNameLower.includes('free');
          // Check if it's pay-as-you-go
          const billingModel = plan.billing_model;
          const pricePerMember = plan.price_per_member_vatu;
          isPayAsYouGo = (billingModel === 'per_member' || planNameLower.includes('pay-as-you-go') || planNameLower.includes('pay as you go')) && (pricePerMember !== null && pricePerMember !== undefined);
          
          // Calculate price based on plan type
          if (isPayAsYouGo && pricePerMember) {
            // Use batched member count for pay-as-you-go
            const groupMemberCount = memberCountMap.get(group.id) || 0;
            const perMemberPrice = parseFloat(String(pricePerMember)) || 0;
            planPrice = groupMemberCount * perMemberPrice;
          } else {
            // Use flat price for non-per-member plans
            planPrice = parseFloat(String(plan.price_vatu)) || 0;
          }
        }
      } else if (group.created_by) {
        // Groups inherit subscription from creator - use batched subscription data
        const userSubscription = subscriptionMap.get(group.created_by);
        
        if (userSubscription) {
          let plan: any = null;
          
          // Prioritize batched plan data (it's more reliable)
          if (userSubscription.plan_id) {
            const fetchedPlan = planMap.get(userSubscription.plan_id);
            if (fetchedPlan) {
              plan = fetchedPlan;
            }
          }
          
          // Fallback to nested query result if batched data not available
          if (!plan && userSubscription.subscription_plans) {
            if (Array.isArray(userSubscription.subscription_plans)) {
              plan = userSubscription.subscription_plans[0];
            } else {
              plan = userSubscription.subscription_plans;
            }
          }
          
          if (plan) {
            planName = plan.plan_name || 'No Plan';
            planType = plan.plan_type || 'free';
            currentPlanId = plan.id || userSubscription.plan_id;
            
            // Check if it's a free plan
            const planNameLower = (plan.plan_name || '').toLowerCase();
            isFreePlan = planType === 'free' || planNameLower.includes('free');
            
            // For pay-as-you-go (per_member billing), calculate price based on THIS group's member count
            const billingModel = plan.billing_model;
            const pricePerMember = plan.price_per_member_vatu;
            
            // Check if it's pay-as-you-go plan
            // Note: pricePerMember can be 0, so we check if it's not null/undefined
            isPayAsYouGo = (billingModel === 'per_member' || planNameLower.includes('pay-as-you-go') || planNameLower.includes('pay as you go')) && (pricePerMember !== null && pricePerMember !== undefined);
            
            if (isPayAsYouGo && pricePerMember) {
              // Use batched member count
              const groupMemberCount = memberCountMap.get(group.id) || 0;
              const perMemberPrice = parseFloat(String(pricePerMember)) || 0;
              planPrice = groupMemberCount * perMemberPrice;
            } else {
              // Use flat price for non-per-member plans
              planPrice = parseFloat(String(plan.price_vatu)) || 0;
            }
          }
        }
      }

      // Get payment status from batched payment data
      let paymentStatus: 'pending' | 'approved' | 'rejected' | 'none' = 'none';
      let lastPaymentDate: string | null = null;

      const payment = paymentMap.get(group.id);
      if (payment) {
        paymentStatus = payment.payment_status || 'none';
        lastPaymentDate = payment.submitted_at;
      }

      // Get subscription status from batched subscription data
      let subscriptionStatus: 'active' | 'expired' | 'pending' | 'locked' = 'pending';
      
      if (group.created_by) {
        const subscription = subscriptionMap.get(group.created_by);

        if (subscription) {
          subscriptionStatus = subscription.subscription_status || 'pending';
          
          // Check if locked
          if (subscription.access_locked) {
            subscriptionStatus = 'locked';
          }
          // Check if expired (only if end_date exists - pay-as-you-go has NULL end_date)
          else if (subscription.end_date) {
            const endDate = new Date(subscription.end_date);
            if (endDate < new Date()) {
              subscriptionStatus = 'expired';
            } else {
              subscriptionStatus = 'active';
            }
          } else {
            // No end_date means pay-as-you-go or unlimited plan - always active
            subscriptionStatus = 'active';
          }
        }
      }

      // Get member count
      const { count: memberCount } = await supabase
        .from('group_members')
        .select('*', { count: 'exact', head: true })
        .eq('group_id', group.id);

        processedGroups.push({
          id: group.id,
          name: group.name || 'Unnamed Group',
          description: group.description || '',
          createdBy: group.created_by || null,
          adminName,
          adminEmail,
          planName,
          planType,
          planPrice,
          planId: currentPlanId,
          paymentStatus,
          lastPaymentDate,
          subscriptionStatus,
          memberCount: memberCount || 0,
          isPayAsYouGo,
          isFreePlan
        });
      } catch (err) {
        console.error(`Error processing group ${group.id} (${group.name}):`, err);
        // Still add the group with default values so it shows up
        processedGroups.push({
          id: group.id,
          name: group.name || 'Unnamed Group',
          description: group.description || '',
          createdBy: group.created_by || null,
          adminName: 'Unknown',
          adminEmail: '',
          planName: 'No Plan',
          planType: 'free',
          planPrice: 0,
          planId: null,
          paymentStatus: 'none',
          lastPaymentDate: null,
          subscriptionStatus: 'pending',
          memberCount: 0,
          isPayAsYouGo: false,
          isFreePlan: true
        });
      }
    }

    // Apply filter
    let filtered = processedGroups;
    if (filter === 'active') {
      filtered = processedGroups.filter(g => g.subscriptionStatus === 'active');
    } else if (filter === 'pending') {
      filtered = processedGroups.filter(g => g.paymentStatus === 'pending' || g.subscriptionStatus === 'pending');
    } else if (filter === 'expired') {
      filtered = processedGroups.filter(g => g.subscriptionStatus === 'expired');
    }

    setGroups(filtered);
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'locked':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Subscription Management - communitySaving</title>
      </Helmet>

      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      <div className="flex">
        <div className="hidden lg:block">
          {/* Treat group_admin and super_admin as admin for sidebar navigation */}
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            userRole="admin"
            isSuperAdmin={isSuperAdmin}
          />
        </div>

        <main
          className={`flex-1 transition-layout ${
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
          }`}
        >
          <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">
                  Subscription Management
                </h1>
                <p className="text-muted-foreground">
                  View groups, their administrators, subscription plans, and payment status
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* For group admins: quick access to member management */}
                {!isSuperAdmin && (
                  <button
                    onClick={() => navigate('/group-admin/members')}
                    className="px-4 py-2 border border-border bg-card text-foreground rounded-sm text-sm font-medium hover:bg-muted transition-colors flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Manage Members
                  </button>
                )}
                {/* For super admins: manage plans */}
                {isSuperAdmin && (
                  <button
                    onClick={() => navigate('/subscription-plan-management')}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
                  >
                    <Package className="w-4 h-4" />
                    Manage Plans
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Filter buttons */}
            <div className="mb-6 flex gap-2">
              {(['all', 'active', 'pending', 'expired'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                    filter === status
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading subscription data...</div>
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Group</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Admin</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Price</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Payment Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Subscription Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Members</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Payment</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-12 text-center text-muted-foreground">
                            No groups found
                          </td>
                        </tr>
                      ) : (
                        groups.map((group) => (
                          <tr key={group.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                            <td className="py-3 px-4">
                              <div className="font-medium text-foreground">{group.name}</div>
                              {group.description && (
                                <div className="text-xs text-muted-foreground mt-1">{group.description}</div>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-sm text-foreground">{group.adminName}</div>
                              <div className="text-xs text-muted-foreground">{group.adminEmail}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-sm font-medium text-foreground">{group.planName}</div>
                              <div className="text-xs text-muted-foreground capitalize">{group.planType}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-sm text-foreground">
                                {group.isFreePlan ? 'Free' : 
                                 group.isPayAsYouGo && group.planPrice === 0 ? '0 VT' :
                                 group.planPrice > 0 ? `${group.planPrice.toLocaleString()} VT` : '0 VT'}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-sm text-xs font-medium ${getPaymentStatusColor(group.paymentStatus)}`}>
                                {getPaymentStatusIcon(group.paymentStatus)}
                                {group.paymentStatus === 'none' ? 'No Payment' : group.paymentStatus.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium ${getSubscriptionStatusColor(group.subscriptionStatus)}`}>
                                {group.subscriptionStatus.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-sm text-foreground">{group.memberCount}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-sm text-foreground">
                                {group.lastPaymentDate 
                                  ? new Date(group.lastPaymentDate).toLocaleDateString()
                                  : 'Never'
                                }
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                onClick={() => {
                                  setSelectedGroupForUpgrade(group);
                                  setUpgradeModalOpen(true);
                                }}
                                className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-sm transition-colors flex items-center gap-1.5"
                              >
                                <ArrowUp className="w-4 h-4" />
                                Upgrade
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Upgrade Plan Modal */}
      {selectedGroupForUpgrade && user && (
        <UpgradePlanModal
          isOpen={upgradeModalOpen}
          onClose={() => {
            setUpgradeModalOpen(false);
            setSelectedGroupForUpgrade(null);
          }}
          currentPlanId={selectedGroupForUpgrade.planId}
          currentPlanName={selectedGroupForUpgrade.planName}
          groupId={selectedGroupForUpgrade.id}
          // Subscriptions are tracked on the group creator/admin user.
          // For super admins upgrading a group, target the group creator's subscription.
          userId={isSuperAdmin ? (selectedGroupForUpgrade.createdBy || user.id) : user.id}
          actorRole={
            isSuperAdmin
              ? 'super_admin'
              : (userProfile?.role === 'group_creator' ? 'group_creator' : 'group_admin')
          }
          onUpgradeSuccess={() => {
            fetchGroupsWithSubscriptions();
          }}
        />
      )}
    </>
  );
};

export default SubscriptionManagement;

